import { PlanType } from "@brookbush/database";

import { useAuth, useUser } from "@clerk/nextjs";
import { Menu, MenuButton, MenuItem, MenuItems } from "@headlessui/react";
import { AnimatePresence, motion } from "framer-motion";
import {
	AwardIcon,
	ChevronRightIcon,
	CoinsIcon,
	GemIcon,
	LogOutIcon,
	MoonIcon,
	SettingsIcon,
	SunIcon,
	SunMoonIcon,
} from "lucide-react";
import { useRouter } from "next/compat/router";
import Image from "next/image";
import Link from "next/link";
import { useTheme } from "next-themes";
import posthog from "posthog-js";

import { useAccess, useLazyIntercom } from "~/hooks";
import { clearCache } from "~/utils/helpers";

const NavbarUserMenu = () => {
	const { user } = useUser();
	const { signOut, userId } = useAuth();
	const { theme, themes, setTheme } = useTheme();

	const path = useRouter()?.asPath.split("?")[0]!;

	const {
		isLoadingAccess,
		isMember,
		isEmployee,
		isFormerMember,
		isPaused,
		isAdmin,
		subscription,
		isTrialing,
	} = useAccess();

	const isIndividual = subscription?.planType === PlanType.INDIVIDUAL;

	const { shutdown, boot } = useLazyIntercom();

	const showMembershipButton = isIndividual
		? (isMember && !isEmployee) || isFormerMember || isPaused
		: isAdmin;

	if (!userId) return null;

	const links = [
		{
			href: "/certificates",
			label: "Certificates",
			icon: AwardIcon,
		},

		{
			href: "/referrals",
			label: "Referrals & Invitations",
			icon: GemIcon,
		},

		{
			href: "/account",
			label: "Account Settings",
			icon: SettingsIcon,
		},

		{
			href: "/membership",
			label: "Membership & Billing",
			icon: CoinsIcon,
		},
	];

	return (
		<div className="relative">
			<Menu>
				{({ open, close }) => (
					<div>
						<MenuButton className="relative -z-0 -my-1 -mr-1 ml-1 flex h-10 w-10 cursor-pointer items-center gap-3 overflow-hidden rounded-full border px-1 py-1 text-left hover:brightness-90">
							<Image
								unoptimized
								src={user?.imageUrl || ""}
								alt="User Image"
								fill
								className="object-cover"
							/>
						</MenuButton>

						<AnimatePresence>
							{open && (
								<MenuItems
									static
									as={motion.div}
									initial={{ opacity: 0, y: -5 }}
									animate={{
										opacity: 1,
										y: 0,
										transition: {
											duration: 0.2,
										},
									}}
									exit={{
										opacity: 0,
										y: -5,
										transition: {
											duration: 0.1,
										},
									}}
									className="absolute top-12 -right-1 z-10 mx-auto flex w-60 flex-col gap-0 rounded-xl border bg-white p-1.5 shadow-black/5 shadow-xl xl:top-12 dark:bg-slate-800"
								>
									<Link
										href={`/profile/${user?.username}`}
										onClick={close}
										className="m-0.5 mb-1 flex items-center justify-between rounded-lg bg-sky-50 py-2 pr-2.5 pl-4 hover:brightness-95 dark:bg-sky-400/10 dark:hover:bg-sky-300/20"
									>
										<div className="flex flex-col items-start gap-0.5">
											<div className="font-medium">
												{user?.firstName} {user?.lastName}
											</div>

											<div className="-mt-0.5 text-sm">
												{isLoadingAccess
													? "Loading"
													: isTrialing
														? "Trial Member"
														: isMember
															? "Member"
															: isFormerMember
																? "Former Member"
																: isPaused
																	? "Paused Member"
																	: "Guest"}
											</div>
										</div>

										<ChevronRightIcon size={20} />
									</Link>

									{links
										.filter(
											(link) =>
												link.href !== "/membership" || showMembershipButton,
										)
										.map(({ href, label, icon: Icon }) => (
											<MenuItem key={href}>
												<Link
													href={href}
													className="flex min-w-[10rem] cursor-pointer items-center gap-2 whitespace-nowrap rounded-lg px-3 py-1.5 font-medium transition-colors hover:bg-sky-50 dark:hover:bg-sky-300/10"
												>
													<Icon size={20} className="shrink-0" /> {label}
												</Link>
											</MenuItem>
										))}

									<MenuItem>
										<button
											onClick={async (e) => {
												e.preventDefault();

												const nextTheme =
													themes[themes.indexOf(theme!) + 1] || themes[0];
												setTheme(nextTheme);
											}}
											className="flex min-w-[10rem] cursor-pointer items-center gap-2 whitespace-nowrap rounded-lg px-3 py-1.5 font-medium hover:bg-sky-50 dark:hover:bg-sky-300/10"
										>
											{theme === "system" ? (
												<>
													<SunMoonIcon size={20} />
													System Theme
												</>
											) : theme === "dark" ? (
												<>
													<MoonIcon size={20} />
													Dark Mode
												</>
											) : (
												<>
													<SunIcon size={20} />
													Light Mode
												</>
											)}
										</button>
									</MenuItem>

									<MenuItem>
										<button
											onClick={async () => {
												await signOut({
													redirectUrl: path,
												});

												clearCache();

												posthog.reset();
												shutdown();
												boot({ hideDefaultLauncher: true });
											}}
											className="flex min-w-[10rem] cursor-pointer items-center gap-2 whitespace-nowrap rounded-lg px-3 py-1.5 font-medium hover:bg-sky-50 dark:hover:bg-sky-300/10"
										>
											<LogOutIcon strokeWidth={2.5} size={18} /> Sign Out
										</button>
									</MenuItem>
								</MenuItems>
							)}
						</AnimatePresence>
					</div>
				)}
			</Menu>
		</div>
	);
};

export default NavbarUserMenu;
